import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "components/button";
import { Header } from "components/header";
import { Footer } from "components/footer/footer";
import * as Accordion from "@radix-ui/react-accordion";
import classNames from "classnames";
import stores from "consts/stores.json";
import { getQueryParams, useCurrentLocation } from "lib/utils";
import Accepted from "assets/landingAccepted.png";
import NotAccepted from "assets/landingNotAccepted.png";
import AuthContext from "contexts/AuthContext";
import ScrollToTop from "components/scroll-to-top";
import weightRestrictions from "consts/weight-restrictions.json";
import { Link, useLocation } from "react-router-dom";
import useIsMobile from "hooks/useIsMobile";
import { TradeInIcon } from "pages/svgs/tradein-logo";
import { COUNTRY_NAME, LOCALE_NAME, STATE_NAME } from "consts/query-params";
import useScrollToAnchor from "hooks/useScrollToAnchor";
import { Helmet } from "react-helmet";
// import LegoWoman from "assets/lego-woman.png";

import PackagingGuide1 from "assets/packagingguide-1.jpg";
import PackagingGuide2 from "assets/packagingguide-2.jpg";
import PackagingGuide2Dropoff from "assets/packagingguide-2-dropoff.jpg";
import PackagingGuide2Kiosk from "assets/packagingguide-2-kiosk.jpg";
import PackagingGuide2KioskPinnacle from "assets/packagingguide-2-kiosk-pinnacle.jpg";
import PackagingGuide3 from "assets/packagingguide-3.jpg";
import PackagingGuide3Dropoff from "assets/packagingguide-3-dropoff.jpg";
import PackagingGuide4 from "assets/packagingguide-4.jpg";

import WhatWeAccept1 from "assets/whatweaccept-1.jpg";
import WhatWeAccept2 from "assets/whatweaccept-2.jpg";
import WhatWeAccept3 from "assets/whatweaccept-3.jpg";
import WhatWeAccept4 from "assets/whatweaccept-4.jpg";
import DontAccept1 from "assets/dontaccept-1.jpg";
import DontAccept2 from "assets/dontaccept-2.jpg";
import DontAccept3 from "assets/dontaccept-3.jpg";
import DontAccept4 from "assets/dontaccept-4.jpg";
import DontAccept5 from "assets/dontaccept-5.jpg";
import DontAccept6 from "assets/dontaccept-6.jpg";
import DontAccept7 from "assets/dontaccept-7.jpg";
import DontAccept8 from "assets/dontaccept-8.jpg";

const PreparationGuidelines = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const { location: currentLocation } = useCurrentLocation();
  const queryParams = getQueryParams(location.search);
  const [takeBackTypeToggle, setTakeBackTypeToggle] = useState(
    queryParams?.preselected === "in-store" ? "store" : "mail"
  );

  const [showInStore, setShowInStore] = useState(false);

  // Accordion triggers/components (copied from landing for consistency)
  const AccordionTrigger = React.forwardRef(
    ({ children, className, ...props }, forwardedRef) => (
      <Accordion.Header className="AccordionHeader text-primary text-2xl">
        <Accordion.Trigger
          className={classNames("AccordionTrigger text-left", className)}
          {...props}
          ref={forwardedRef}
        >
          <div>{children}</div>
          <span
            className="AccordionChevron material-symbols-outlined align-bottom ml-large text-secondary"
            aria-hidden
          >
            keyboard_arrow_down
          </span>
        </Accordion.Trigger>
      </Accordion.Header>
    )
  );

  const AccordionContent = React.forwardRef(
    ({ children, className, ...props }, forwardedRef) => (
      <Accordion.Content
        className={classNames("AccordionContent", className)}
        {...props}
        ref={forwardedRef}
      >
        <div className="AccordionContentText text-secondary mb-regular">
          {children}
        </div>
      </Accordion.Content>
    )
  );

  const store = currentLocation?.takebackMethod?.find((method) =>
    method.startsWith("in_store")
  )
    ? stores.find(
        (store) =>
          store.value ===
          currentLocation.takebackMethod.find((method) =>
            method.startsWith("in_store")
          )
      )
    : null;
  const storeLocation = store?.name;
  const storeAddress = store?.address;

  const updateShowInStore = useCallback(() => {
    setShowInStore(
      currentLocation.takebackMethod.some((method) =>
        stores.some((store) => store.value === method)
      )
    );
  }, [currentLocation]);

  useEffect(() => {
    updateShowInStore();
  }, [updateShowInStore]);

  // Handle anchor scrolling when page loads
  useScrollToAnchor();

  const isKiosk = currentLocation?.takebackMethod
    ? stores.some(
        (store) =>
          currentLocation.takebackMethod.includes(store.value) &&
          store.return_type === "kiosk"
      )
    : false;

  const { user } = useContext(AuthContext);

  const maxWeight =
    store && store?.return_type === "kiosk"
      ? weightRestrictions.find((weight) => weight.unit === "lbs").maxKiosk
      : store && store?.return_type === "drop_off"
        ? weightRestrictions.find((weight) => weight.unit === "lbs").maxDropOff
        : weightRestrictions.find((weight) => weight.unit === "lbs").maxMail;

  const isMobile = useIsMobile();
  const navigationLink = `${t("urls.my_trade_ins")}/${location.search}`;
  const mobileContent = (
    <>
      <Link to={navigationLink}>
        <TradeInIcon />
      </Link>
    </>
  );

  const CtaButton = ({ className }) => {
    return (
      <Button
        tracking="Start trade-in clicked"
        href={`${t("urls.trade_in_weight")}`}
        queryParams={{
          [LOCALE_NAME]: currentLocation.locale,
          [STATE_NAME]: currentLocation.state,
          [COUNTRY_NAME]: currentLocation.country,
        }}
        className={className}
      >
        {t("landing.button_start_new")}
      </Button>
    );
  };

  return (
    <div>
      <ScrollToTop />
      <Helmet>
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "HowTo",
            "name": "Preparation Guidelines",
            "description": "Grab your pre-loved LEGO® bricks and review what’s accepted and how to prepare your trade-in.",
            "url": "https://legobricktakeback.com/en-US/preparation-guidelines"
          }
        `}</script>
      </Helmet>
      <Header mobileChildren={mobileContent} classNames="bg-yellow border-none">
        <div className="flex gap-large">
          <CtaButton className="hidden lg:block" />

          {!isMobile && (
            <Button
              href={user ? `${t("urls.my_trade_ins")}` : `${t("urls.login")}`}
              variant="secondary_no_border"
              queryParams={queryParams}
            >
              {user
                ? t("landing.button_my_trade_ins")
                : t("landing.button_sign_in")}
            </Button>
          )}
        </div>
      </Header>
      <div className="py-12 bg-blue-300/25 flex flex-col items-center">
        <h1 className="text-primary text-[30px] md:text-[40px] font-bold text-center py-medium">
          Preparation Guidelines
        </h1>
        <p className="text-xl max-w-lg text-center text-secondary">
          Grab your pre-loved LEGO® bricks and review what’s accepted and how
          to prepare your trade-in.
        </p>
      </div>

      <div id="howItWorks" className="">
        <div className="flex flex-col justify-center px-x-large md:px-xx-large ">
          {showInStore ? (
            <div className="mt-xx-large md:mt-xxx-large">
              <div className="mb-x-large md:mb-xx-large pb-xxx-large">
                <h3 className="text-primary text-2xl font-bold md:text-4xl text-center">
                  Choose your method to see how to prepare your trade-in:
                </h3>
                <div className="flex flex-col md:flex-row lg:max-w-4xl mx-auto mt-regular md:mt-large bg-gray-300/25 border border-gray-300 rounded-xl p-x-small text-primary">
                  <button
                    onClick={() => setTakeBackTypeToggle("mail")}
                    className={`${
                      takeBackTypeToggle === "mail" ? "bg-blue text-white " : ""
                    }rounded-lg py-regular px-x-large text-2xl flex flex-1 items-center justify-center`}
                  >
                    <span
                      className={`material-symbols-outlined align-bottom mr-large scale-125 ${
                        takeBackTypeToggle === "mail"
                          ? "text-white"
                          : "text-[#757575]"
                      }`}
                    >
                      package_2
                    </span>
                    <span className="block truncate leading-10">
                      {t("landing.method_mail")}
                    </span>
                  </button>
                  <button
                    onClick={() => setTakeBackTypeToggle("store")}
                    className={`${
                      takeBackTypeToggle === "store"
                        ? "bg-blue text-white "
                        : ""
                    }rounded-lg py-regular px-x-large text-2xl flex flex-1 items-center justify-center`}
                  >
                    <span
                      className={`material-symbols-outlined align-bottom mr-large scale-125 ${
                        takeBackTypeToggle === "store"
                          ? "text-white"
                          : "text-[#757575]"
                      }`}
                    >
                      storefront
                    </span>
                    <span className="block truncate leading-10">
                      {t("landing.method_store", {
                        store: storeLocation,
                        asterisk: takeBackTypeToggle === "store" ? "*" : "",
                      })}
                    </span>
                  </button>
                </div>

                {takeBackTypeToggle === "store" && (
                  <div className="text-black-300 w-full text-center mt-large text-sm">
                    <span className="font-semibold ">*Address:</span>{" "}
                    {storeAddress}
                  </div>
                )}
              </div>
            </div>
          ) : (
            ""
          )}
        </div>
      </div>
      <div className="py-xx-large flex flex-col gap-xxxx-large">
        <div id="preparation" className="px-x-large md:px-xx-large">
          <div className="flex flex-col justify-center lg:max-w-6xl mx-auto">
            <div className="mt-xx-large md:mt-xxx-large">
              {/* <h3 className="text-primary text-2xl font-bold md:text-4xl pb-xx-large">
                {takeBackTypeToggle === "store"
                  ? "In-Store preparation guides"
                  : "Mail-In preparation guides"}
              </h3> */}
              <Accordion.Root
                type="multiple"
                defaultValue={!isKiosk ? ["item-1", "item-2"] : ["item-1"]}
                collapsible
              >
                <Accordion.Item
                  className="gap-0 border border-[#e4e4e4]"
                  value="item-1"
                  id="what-we-accept"
                >
                  <AccordionTrigger className="justify-between w-full border-b border-gray-300 px-4 py-5 flex items-center">
                    <div className="flex items-center gap-1">
                      <div className="bg-red text-white rounded-full w-6 h-6 flex items-center justify-center shrink-0 mr-4">
                        1
                      </div>
                      <div className="font-bold text-3xl">
                        Acceptance criteria
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="bg-gray-300/25 border-b border-gray-300 p-4">
                    <div className="flex flex-col space-y-4 border-b border-gray-300 pb-x-large">
                      <h3 className="text-primary text-xl md:text-2xl pt-x-large flex items-center gap-4">
                        <img src={Accepted} alt="" className="w-8 h-8" />
                        <div className="font-bold">What we accept</div>
                      </h3>
                      <p>
                        <strong>
                          Fully disassembled, clean and undamaged LEGO bricks or
                          elements,
                        </strong>{" "}
                        including:
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-large mt-regular">
                        <div className="flex flex-col items-center text-center">
                          <div className="bg-green-300 p-6 w-full aspect-[6/5] flex items-center justify-center mb-regular">
                            <img
                              src={WhatWeAccept1}
                              alt="Bricks and elements"
                              className="max-w-full max-h-full object-cover"
                            />
                          </div>
                          <h4 className="text-primaryew text-lg mb-small">
                            Bricks and elements
                          </h4>
                          <p className="text-tertiary text-sm">
                            Including standard bricks and tiles, with and
                            without stickers.{" "}
                            <strong>
                              All bricks must be fully disassembled and cannot
                              be attached to any other piece.
                            </strong>
                          </p>
                        </div>

                        <div className="flex flex-col items-center text-center">
                          <div className="bg-green-300 p-6 w-full aspect-[6/5] flex items-center justify-center mb-regular">
                            <img
                              src={WhatWeAccept2}
                              alt="Wheels, tires, doors, and windows"
                              className="max-w-full max-h-full object-cover"
                            />
                          </div>
                          <h4 className="text-primaryew text-lg mb-small">
                            Wheels, tires, doors, and windows
                          </h4>
                          <p className="text-tertiary text-sm">
                            All parts must be separated from each other, such as
                            tires from rims and doors from door frames.
                          </p>
                        </div>

                        <div className="flex flex-col items-center text-center">
                          <div className="bg-green-300 p-6 w-full aspect-[6/5] flex items-center justify-center mb-regular">
                            <img
                              src={WhatWeAccept3}
                              alt="Minifigures and Mini-dolls"
                              className="max-w-full max-h-full object-cover"
                            />
                          </div>
                          <h4 className="text-primaryew text-lg mb-small">
                            Minifigures and Mini-dolls
                          </h4>
                          <p className="text-tertiary text-sm">
                            Can include accessories but must be separated from
                            Minifigures. Minifigures do not need to be
                            disassembled for the trade-in.
                          </p>
                        </div>

                        <div className="flex flex-col items-center text-center">
                          <div className="bg-green-300 p-6 w-full aspect-[6/5] flex items-center justify-center mb-regular">
                            <img
                              src={WhatWeAccept4}
                              alt="Baseplates"
                              className="max-w-full max-h-full object-cover"
                            />
                          </div>
                          <h4 className="text-primaryew text-lg mb-small">
                            Baseplates
                          </h4>
                          <p className="text-tertiary text-sm">All sizes</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col space-y-4 pt-xxx-large pb-xx-large">
                      <h3 className="text-primary text-xl md:text-2xl flex items-center gap-4">
                        <img src={NotAccepted} alt="" className="w-8 h-8" />
                        <div className="font-bold">What we don't accept</div>
                      </h3>
                      <p className="">
                        We will not accept any{" "}
                        <strong>non-LEGO bricks or toys</strong>, or any of the
                        following LEGO items:
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-large mt-regular">
                        <div className="flex flex-col items-center text-center">
                          <div className="bg-[#F1C6B2] p-6 w-full aspect-[6/5] flex items-center justify-center mb-regular">
                            <img
                              src={DontAccept1}
                              alt="Any connected LEGO bricks or elements"
                              className="max-w-full max-h-full object-cover"
                            />
                          </div>
                          <h4 className="text-primaryew text-lg mb-small">
                            Connected LEGO bricks or elements
                          </h4>
                          <p className="text-tertiary text-sm">
                            All bricks must be disassembled.
                          </p>
                        </div>

                        <div className="flex flex-col items-center text-center">
                          <div className="bg-[#F1C6B2] p-6 w-full aspect-[6/5] flex items-center justify-center mb-regular">
                            <img
                              src={DontAccept2}
                              alt="LEGO® DUPLO® elements or toys"
                              className="max-w-full max-h-full object-cover"
                            />
                          </div>
                          <h4 className="text-primaryew text-lg mb-small">
                            LEGO® DUPLO® bricks or elements
                          </h4>
                          {/* <p className="text-tertiary text-sm">
                            Including PRIMO®, or Quatro® bricks
                          </p> */}
                        </div>

                        <div className="flex flex-col items-center text-center">
                          <div className="bg-[#F1C6B2] p-6 w-full aspect-[6/5] flex items-center justify-center mb-regular">
                            <img
                              src={DontAccept3}
                              alt="Dirty or broken LEGO bricks or elements"
                              className="max-w-full max-h-full object-cover"
                            />
                          </div>
                          <h4 className="text-primaryew text-lg mb-small">
                            LEGO bricks or elements in poor condition
                          </h4>
                          <p className="text-tertiary text-sm">
                            Dirty, worn, broken, yellowed, or stained.
                          </p>
                        </div>

                        <div className="flex flex-col items-center text-center">
                          <div className="bg-[#F1C6B2] p-6 w-full aspect-[6/5] flex items-center justify-center mb-regular">
                            <img
                              src={DontAccept4}
                              alt="Any LEGO packaging"
                              className="max-w-full max-h-full object-cover"
                            />
                          </div>
                          <h4 className="text-primaryew text-lg mb-small">
                            LEGO packaging
                          </h4>
                          <p className="text-tertiary text-sm">&nbsp;</p>
                        </div>

                        <div className="flex flex-col items-center text-center">
                          <div className="bg-[#F1C6B2] p-6 w-full aspect-[6/5] flex items-center justify-center mb-regular">
                            <img
                              src={DontAccept5}
                              alt="Any LEGO or non-LEGO batteries, electronic or metal components"
                              className="max-w-full max-h-full object-cover"
                            />
                          </div>
                          <h4 className="text-primaryew text-lg mb-small">
                            Any batteries, electronic or metal components
                          </h4>
                          <p className="text-tertiary text-sm">
                            Such as motors, sensors, wires and any LEGO pieces
                            integrated with metal.
                          </p>
                        </div>

                        <div className="flex flex-col items-center text-center">
                          <div className="bg-[#F1C6B2] p-6 w-full aspect-[6/5] flex items-center justify-center mb-regular">
                            <img
                              src={DontAccept6}
                              alt="Any LEGO building instructions"
                              className="max-w-full max-h-full object-cover"
                            />
                          </div>
                          <h4 className="text-primaryew text-lg mb-small">
                            LEGO building instructions
                          </h4>
                          <p className="text-tertiary text-sm">&nbsp;</p>
                        </div>

                        <div className="flex flex-col items-center text-center">
                          <div className="bg-[#F1C6B2] p-6 w-full aspect-[6/5] flex items-center justify-center mb-regular">
                            <img
                              src={DontAccept7}
                              alt="Any LEGO storage containers"
                              className="max-w-full max-h-full object-cover"
                            />
                          </div>
                          <h4 className="text-primaryew text-lg mb-small">
                            LEGO storage containers
                          </h4>
                          <p className="text-tertiary text-sm">&nbsp;</p>
                        </div>

                        <div className="flex flex-col items-center text-center">
                          <div className="bg-[#F1C6B2] p-6 w-full aspect-[6/5] flex items-center justify-center mb-regular">
                            <img
                              src={DontAccept8}
                              alt="Any LEGO merchandise"
                              className="max-w-full max-h-full object-cover"
                            />
                          </div>
                          <h4 className="text-primaryew text-lg mb-small">
                            LEGO merchandise
                          </h4>
                          <p className="text-tertiary text-sm">
                            Such as keychains, clothing, or any other LEGO
                            branded items.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* <div className="text-blue cursor-pointer py-large">
                      Read our full guide on what is eligible for a reward
                    </div> */}
                  </AccordionContent>
                </Accordion.Item>
                <div
                  id="packagingGuidelines"
                  className="relative -top-24"
                ></div>
                <div className="mt-x-large">
                  <Accordion.Item
                    className="gap-0 border border-[#e4e4e4]"
                    value="item-2"
                    // id="packaging-guidelines"
                  >
                    <AccordionTrigger className="flex justify-between w-full border-b border-gray-300 px-4 py-5">
                      <div className="flex items-center gap-1">
                        <div className="bg-red text-white rounded-full w-6 h-6 flex items-center justify-center shrink-0 mr-4">
                          2
                        </div>
                        <div className="font-bold text-3xl">
                          Packaging guidelines
                        </div>
                      </div>
                    </AccordionTrigger>
                    {!isKiosk && takeBackTypeToggle !== "mail" ? (
                      <AccordionContent className="bg-gray-300/25 border-b border-gray-300 p-4">
                        <div className="flex flex-col space-y-4 py-x-large">
                          <p className="text-primary">
                            Get your LEGO bricks ready for trade-in. For
                            in-store drop-off, each trade-in must be in a sturdy
                            cardboard shipping box and not exceed{" "}
                            {maxWeight.toFixed(2)} lbs. Here's how:
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-large mt-regular">
                            {/* Item 1 */}
                            <div className="flex flex-col items-center text-center">
                              <div className="bg-gray-300 p-6 w-full aspect-[6/5] flex items-center justify-center mb-regular">
                                <img
                                  src={PackagingGuide1}
                                  alt="Make sure your package weighs no more than X lbs"
                                  className="max-w-full max-h-full object-cover"
                                />
                              </div>
                              <p className="text-secondary text-sm">
                                Ensure the weight of your bricks does not exceed{" "}
                                {maxWeight.toFixed(2)} lbs.{" "}
                                <Link
                                  to={{
                                    pathname: t("urls.trade_in_weight"),
                                    search: `?${new URLSearchParams(queryParams).toString()}`,
                                  }}
                                  className="text-blue cursor-pointer"
                                >
                                  Start another trade-in
                                </Link>{" "}
                                if you have more to send in.
                              </p>
                            </div>
                            {/* Item 2 */}
                            <div className="flex flex-col items-center text-center">
                              <div className="bg-gray-300 p-6 w-full aspect-[6/5] flex items-center justify-center mb-regular">
                                <img
                                  src={PackagingGuide2Dropoff}
                                  alt="Seal your package with strong packing tape"
                                  className="max-w-full max-h-full object-cover"
                                />
                              </div>
                              <p className="text-secondary text-sm">
                                Use a sturdy cardboard shipping box and leave
                                unsealed.
                              </p>
                            </div>
                            {/* Item 3 */}
                            <div className="flex flex-col items-center text-center">
                              <div className="bg-gray-300 p-6 w-full aspect-[6/5] flex items-center justify-center mb-regular">
                                <img
                                  src={PackagingGuide3Dropoff}
                                  alt="Print your shipping label or have your shipping QR code handy"
                                  className="max-w-full max-h-full object-cover"
                                />
                              </div>
                              <p className="text-secondary text-sm">
                                Have your store check-in QR code ready for the
                                Brick Specialist.
                              </p>
                            </div>
                          </div>
                        </div>
                      </AccordionContent>
                    ) : isKiosk && takeBackTypeToggle !== "mail" ? (
                      <AccordionContent className="bg-gray-300/25 border-b border-gray-300 p-4">
                        <div className="flex flex-col space-y-4 py-x-large">
                          <p className="text-primary">
                            Get your LEGO bricks ready for trade-in. For
                            in-store, each trade-in must be in an easy-to-carry
                            container and not exceed {maxWeight.toFixed(2)} lbs.
                            Here's how:
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-large mt-regular">
                            {/* Item 1 */}
                            <div className="flex flex-col items-center text-center">
                              <div className="bg-gray-300 p-6 w-full aspect-[6/5] flex items-center justify-center mb-regular">
                                <img
                                  src={PackagingGuide2Dropoff}
                                  alt="Make sure your package weighs no more than X lbs"
                                  className="max-w-full max-h-full object-cover"
                                />
                              </div>
                              <p className="text-secondary text-sm">
                                Place your trade-in in an easy-to-carry
                                container.
                              </p>
                            </div>
                            {/* Item 2 */}
                            <div className="flex flex-col items-center text-center">
                              <div className="bg-gray-300 p-6 w-full aspect-[6/5] flex items-center justify-center mb-regular">
                                <img
                                  src={
                                    currentLocation.state === "AR"
                                      ? PackagingGuide2KioskPinnacle
                                      : PackagingGuide2Kiosk
                                  }
                                  alt="Seal your package with strong packing tape"
                                  className="max-w-full max-h-full object-cover"
                                />
                              </div>
                              <p className="text-secondary text-sm">
                                Visit the LEGO Store {storeLocation} at your
                                appointment date and time.
                              </p>
                            </div>
                            {/* Item 3 */}
                            <div className="flex flex-col items-center text-center">
                              <div className="bg-gray-300 p-6 w-full aspect-[6/5] flex items-center justify-center mb-regular">
                                <img
                                  src={PackagingGuide3Dropoff}
                                  alt="Print your shipping label or have your shipping QR code handy"
                                  className="max-w-full max-h-full object-cover"
                                />
                              </div>
                              <p className="text-secondary text-sm">
                                Have your store check-in QR code ready for the
                                Brick Specialist.
                              </p>
                            </div>
                          </div>
                        </div>
                      </AccordionContent>
                    ) : (
                      <AccordionContent className="bg-gray-300/25 border-b border-gray-300 p-4">
                        <div className="flex flex-col space-y-4 py-x-large">
                          <p className="text-primary">
                            Get your LEGO bricks ready for trade-in. You’ll
                            receive a free shipping label or QR code, just make
                            sure your package is packed properly. Here's how:
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-large mt-regular">
                            {/* Item 1 */}
                            <div className="flex flex-col items-center text-center">
                              <div className="bg-gray-300 p-6 w-full aspect-[6/5] flex items-center justify-center mb-regular">
                                <img
                                  src={PackagingGuide1}
                                  alt="Make sure your package weighs no more than X lbs"
                                  className="max-w-full max-h-full object-cover"
                                />
                              </div>
                              <p className="text-secondary text-sm">
                                Ensure the weight of your bricks does not exceed{" "}
                                {
                                  weightRestrictions.find(
                                    (weight) => weight.unit === "lbs"
                                  ).maxMail
                                }{" "}
                                lbs.{" "}
                                <Link
                                  to={{
                                    pathname: t("urls.trade_in_weight"),
                                    search: `?${new URLSearchParams(queryParams).toString()}`,
                                  }}
                                  className="text-blue cursor-pointer"
                                >
                                  Start another trade-in
                                </Link>{" "}
                                if you have more to send in.
                              </p>
                            </div>
                            {/* Item 2 */}
                            <div className="flex flex-col items-center text-center">
                              <div className="bg-gray-300 p-6 w-full aspect-[6/5] flex items-center justify-center mb-regular">
                                <img
                                  src={PackagingGuide2}
                                  alt="Seal your package with strong packing tape"
                                  className="max-w-full max-h-full object-cover"
                                />
                              </div>
                              <p className="text-secondary text-sm">
                                Secure your bricks in a sturdy cardboard
                                shipping box and seal with strong packing tape.
                              </p>
                            </div>
                            {/* Item 3 */}
                            <div className="flex flex-col items-center text-center">
                              <div className="bg-gray-300 p-6 w-full aspect-[6/5] flex items-center justify-center mb-regular">
                                <img
                                  src={PackagingGuide3}
                                  alt="Print your shipping label or have your shipping QR code handy"
                                  className="max-w-full max-h-full object-cover"
                                />
                              </div>
                              <p className="text-secondary text-sm">
                                Print your shipping label or have your shipping
                                QR code ready when you bring it to your nearest
                                UPS location.
                              </p>
                            </div>
                            {/* Item 4 */}
                            <div className="flex flex-col items-center text-center">
                              <div className="bg-gray-300 p-6 w-full aspect-[6/5] flex items-center justify-center mb-regular">
                                <img
                                  src={PackagingGuide4}
                                  alt="Remember, one trade-in = one package = one UPS shipment"
                                  className="max-w-full max-h-full object-cover"
                                />
                              </div>
                              <p className="text-secondary text-sm">
                                Your UPS label or QR code is for one package
                                only (up to {maxWeight.toFixed(2)} lbs). Need to
                                send more?{" "}
                                <Link
                                  to={{
                                    pathname: t("urls.trade_in_weight"),
                                    search: `?${new URLSearchParams(queryParams).toString()}`,
                                  }}
                                  className="text-blue cursor-pointer"
                                >
                                  Start another trade-in
                                </Link>{" "}
                                to get an additional label or QR code.
                              </p>
                            </div>
                          </div>
                        </div>
                      </AccordionContent>
                    )}
                  </Accordion.Item>
                </div>
              </Accordion.Root>
            </div>
          </div>
        </div>

        <div className="px-xx-large flex flex-col justify-center items-center space-y-x-large mb-xx-large">
          <h3 className="text-primary text-2xl font-bold md:text-4xl text-center">
            Turn your bricks into rewards
          </h3>
          <CtaButton />
        </div>

        {/* <div className="border border-gray-300 rounded-xl flex relative max-w-2xl md:pr-[282px] mt-large p-1 mx-6 md:mx-auto mb-xxxx-large">
          <div className="p-x-large flex flex-col gap-regular w-full space-y-small">
            <h3 className="text-primary text-2xl font-bold md:text-4xl">
              Got questions?
            </h3>
            <p className="text-primary">
              Learn how to prepare your trade-in and more details on the LEGO
              Brick Take Back program.
            </p>
            <Link
              to={{
                pasthname: t("urls.faq"),
                search: `?${new URLSearchParams(queryParams).toString()}`,
              }}
              className="select-none flex items-center justify-center whitespace-nowrap rounded-full font-medium ring-offset-white transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 dark:ring-offset-gray-950 dark:focus-visible:ring-gray-300 bg-blue hover:bg-blue-600 text-white disabled:bg-blue-300 w-fit px-[24px] py-regular leading-7"
            >
              Visit FAQ
            </Link>
          </div>
          <img
            src={LegoWoman}
            alt="Smiling LEGO Minifigure"
            className="md:absolute md:right-large md:bottom-0 w-[282px] md:w-[246px] hidden sm:block"
          />
        </div> */}
      </div>
      <Footer />
    </div>
  );
};

export default PreparationGuidelines;
